import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import img1 from "../schoolgirl.jpg";

const CSS_Opacity = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🌫️ CSS Opacity</h1>
      <p className="tutorial-intro">
        The <code>opacity</code> property in CSS sets the transparency level of
        an element. It ranges from <code>0</code> (completely transparent) to{" "}
        <code>1</code> (fully visible).
      </p>
      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🌟 Why Use CSS Opacity?
        </h2>
        <p>
          Opacity helps you control how visible an element should be. It’s often
          used for hover effects, overlays, disabled states, and smoother UI
          designs.
        </p>
      </div>
      {/* Common Uses of Opacity */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          📘 Common Uses of CSS Opacity
        </h2>
        <ul style={{ lineHeight: "1.8", marginLeft: "1.2rem" }}>
          <li>
            Create <b>hover effects</b> (fade in/out when the mouse is over an
            element)
          </li>
          <li>
            Make <b>disabled buttons</b> look less prominent
          </li>
          <li>
            Add <b>transparent overlays</b> on top of images or sections
          </li>
          <li>
            Smooth <b>transitions</b> when showing/hiding elements
          </li>
          <li>
            Combine with <code>background</code> or <code>z-index</code> for{" "}
            <b>layered designs</b>
          </li>
        </ul>
      </div>
      {/* Example 1: Basic Opacity */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Example 1: Basic Opacity</h2>
        <p>
          The opacity value ranges from <code>0</code> (invisible) to{" "}
          <code>1</code> (fully visible).
        </p>

        <CodeCopyBlock
          code={`.box1 { opacity: 1; }   /* Fully visible */
.box2 { opacity: 0.5; } /* 50% transparent */
.box3 { opacity: 0; }   /* Fully invisible */`}
        />

        <div style={{ display: "flex", gap: "20px", marginTop: "1rem" }}>
          <div
            style={{
              width: "100px",
              height: "100px",
              background: "#60a5fa",
              opacity: 1,
              textAlign: "center",
              lineHeight: "100px",
              color: "white",
              borderRadius: "6px",
            }}
          >
            1
          </div>
          <div
            style={{
              width: "100px",
              height: "100px",
              background: "#34d399",
              opacity: 0.5,
              textAlign: "center",
              lineHeight: "100px",
              color: "white",
              borderRadius: "6px",
            }}
          >
            0.5
          </div>
          <div
            style={{
              width: "100px",
              height: "100px",
              background: "#f87171",
              opacity: 0,
              textAlign: "center",
              lineHeight: "100px",
              color: "white",
              borderRadius: "6px",
            }}
          >
            0
          </div>
        </div>
      </div>
      {/* Example 2: Hover Effect */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Example 2: Hover Effect</h2>
        <p>
          Opacity is commonly used to create hover effects where an element
          fades in/out.
        </p>

        <CodeCopyBlock
          code={`.hover-box {
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.hover-box:hover {
  opacity: 1;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <div
            style={{
              width: "150px",
              height: "100px",
              background: "#fbbf24",
              opacity: 0.6,
              transition: "opacity 0.3s ease",
              textAlign: "center",
              lineHeight: "100px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Hover Me
          </div>
        </div>
      </div>
      export default CSS_Opacity;
      {/* Example 1: Basic Opacity */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Opacity</h2>
        <p>
          A simple example showing different levels of <code>opacity</code>.
        </p>

        <CodeCopyBlock
          code={`.box1 { opacity: 1; }     /* Fully visible */
.box2 { opacity: 0.5; }   /* 50% transparent */
.box3 { opacity: 0.2; }   /* Very transparent */`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              width: "100px",
              height: "100px",
              background: "#60a5fa",
              opacity: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              borderRadius: "8px",
            }}
          >
            1
          </div>
          <div
            style={{
              width: "100px",
              height: "100px",
              background: "#60a5fa",
              opacity: 0.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              borderRadius: "8px",
            }}
          >
            0.5
          </div>
          <div
            style={{
              width: "100px",
              height: "100px",
              background: "#60a5fa",
              opacity: 0.2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              borderRadius: "8px",
            }}
          >
            0.2
          </div>
        </div>
      </div>
      {/* Example 2: Hover Effect */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Hover Opacity Effect</h2>
        <p>
          Use opacity to create interactive hover effects for buttons or images.
        </p>

        <CodeCopyBlock
          code={`img {
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

img:hover {
  opacity: 1;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <img
            src={img1}
            alt="Hover Example"
            style={{
              opacity: 0.7,
              transition: "opacity 0.3s ease",
              borderRadius: "6px",
              border: "2px solid #2563eb",
            }}
          />
        </div>
      </div>
      {/* Example 3: Background Opacity with RGBA */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Background Opacity (Using RGBA)</h2>
        <p>
          If you set <code>opacity</code> on a parent, all children inherit the
          transparency. To make only the <b>background</b> transparent, use{" "}
          <code>rgba()</code> colors.
        </p>

        <CodeCopyBlock
          code={`.box {
  background-color: rgba(96, 165, 250, 0.5); /* 50% background only */
  color: black; /* text stays solid */
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            padding: "20px",
            background: "rgba(96, 165, 250, 0.5)",
            border: "2px solid #2563eb",
            borderRadius: "6px",
          }}
        >
          <p>
            This box uses <code>rgba()</code> for transparency. The text is{" "}
            <b>not faded</b>.
          </p>
        </div>
      </div>
      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          After learning opacity, explore{" "}
          <a href="/css/transition" className="learn-more">
            CSS Transitions
          </a>{" "}
          and{" "}
          <a href="/css/animation" className="learn-more">
            CSS Animations
          </a>{" "}
          to create smooth fade-in/out effects.
        </p>
      </div>
    </div>
  );
};

export default CSS_Opacity;
